import React, { useState, useEffect, useCallback, useMemo } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useNotifications } from "@/lib/notifications";
import PasskeyEnrollModal from "@/components/PasskeyEnrollModal";
import FeedbackButton from "@/components/FeedbackButton";
import ProOnboardingModal from "@/components/ProOnboardingModal";
import UserAvatar from "@/components/UserAvatar";
import SidebarCalendar from "@/components/SidebarCalendar";
import {
  House, Buildings, Storefront, Kanban, Users as UsersIcon,
  Calendar, CurrencyEur, Gear, SignOut, ShieldCheck, PackageIcon, Bell, Robot, List, Bug, Envelope,
} from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: House, key: "dashboard", roles: ["all"] },
  // Iter209af — Client email a tutto schermo (mock statico); visibile a tutti
  // gli utenti autenticati (pro, operatori, admin).
  { to: "/email", label: "Email", icon: Envelope, key: "email", roles: ["all"] },
  { to: "/properties", label: "Immobili", icon: Buildings, key: "properties", roles: ["professional"] },
  // Iter208.86 — Rubrica clienti del pro
  { to: "/clients", label: "Clienti", icon: UsersIcon, key: "clients", roles: ["professional"] },
  { to: "/marketplace", label: "Marketplace", icon: Storefront, key: "marketplace", roles: ["professional"] },
  { to: "/admin/marketplace", label: "Marketplace PRO", icon: Storefront, key: "admin-marketplace", roles: ["admin"], allowSupervisor: true },
  { to: "/board", label: "Bacheca Pratiche", icon: Kanban, key: "board", roles: ["admin", "tech_operator", "desk_operator", "site_operator"] },
  { to: "/calendar", label: "Calendario", icon: Calendar, key: "calendar", roles: ["site_operator", "admin"] },
  { to: "/services-admin", label: "Servizi", icon: PackageIcon, key: "services-admin", roles: ["admin"] },
  { to: "/users", label: "Utenti", icon: UsersIcon, key: "users", roles: ["admin"] },
  { to: "/admin/chatbot", label: "Chatbot", icon: Robot, key: "admin-chatbot", roles: ["admin"] },
  // Iter209bk — "Registro attività" spostato nelle Impostazioni admin
  // (tab audit-log). Voce sidebar rimossa; la route redirige a /settings.
  { to: "/admin/feedback", label: "Feedback Beta", icon: Bug, key: "admin-feedback", roles: ["admin"] },
  { to: "/accounting", label: "Contabilità", icon: CurrencyEur, key: "accounting", roles: ["accounting_operator", "admin"], denySupervisor: true },
  { to: "/team", label: "Team", icon: UsersIcon, key: "team", roles: ["professional"] },
  // Iter208.18 — Visibile solo per PRO con dashboard_type="agenzia" (agenzie
  // immobiliari). Il filtro dashboard_type viene applicato a runtime nella
  // logica di rendering del NAV (vedi filteredNav più sotto).
  // Iter208.99 — Voce "My Domain" rimossa: la pagina è stata spostata nella
  // tab "Abbonamenti" delle Impostazioni del Pro Master.
  { to: "/my-compensations", label: "I miei compensi", icon: CurrencyEur, key: "my-compensations", roles: ["out_operator"] },
  // Iter208.65 — Voce "Impostazioni" rimossa dal menu laterale: la pagina
  // /settings resta accessibile cliccando sul nome utente in fondo alla
  // sidebar (user-tile) come già avviene.
];

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "ora";
  if (diff < 3600) return `${Math.floor(diff / 60)}m fa`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h fa`;
  return `${Math.floor(diff / 86400)}g fa`;
}

// ---- Custom hook: polling chatbot pending count (solo admin/supervisor) ----
function useChatbotPending(user) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (user?.role !== "admin" && !user?.is_supervisor) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const { api } = await import("@/lib/api");
        const r = await api.get("/admin/chatbot/stats");
        if (!cancelled) setCount(r.data?.pending_contacts || 0);
      } catch (e) {
        if (process.env.NODE_ENV !== "production") console.warn("[layout] chatbot stats poll failed:", e);
      }
    };
    load();
    const t = setInterval(load, 25000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user]);
  return count;
}

// ---- Visibility helper ----
function canSee(roles, user, opts = {}) {
  if (roles.includes("all")) return true;
  if (roles.includes("out_operator")) {
    return user.operator_type === "out" && user.role !== "professional";
  }
  if (roles.length === 1) return roles[0] === user.role;
  // Supervisor override generico, tranne quando l'item ha `denySupervisor: true`
  // (es. Contabilità è visibile solo a admin puro e accounting_operator).
  if (user.is_supervisor && !opts.denySupervisor) return true;
  return roles.includes(user.role);
}

// ==================== Sub-components ====================

function SidebarBrand() {
  return (
    <NavLink to="/dashboard" className="px-5 py-6 border-b border-white/10 block hover:bg-white/[0.03] transition-colors" data-testid="sidebar-brand">
      <div className="flex items-center gap-2.5">
        <img src="/logo.png" alt="HouseTag" className="w-9 h-9 object-contain drop-shadow-lg" />
        <div>
          <div className="font-display text-xl font-bold tracking-tight text-white leading-none">
            Housetag<span style={{ color: "#a78bfa" }}>.it</span>
          </div>
          <div className="text-[10px] mt-1 tracking-[0.18em] uppercase font-semibold" style={{ color: "#8b95a8" }}>
            Real Estate Suite
          </div>
        </div>
      </div>
    </NavLink>
  );
}

function NavList({ visibleNav, badgeFor }) {
  return (
    <nav className="min-h-0 shrink px-3 py-4 space-y-1 overflow-y-auto no-scrollbar">
      {visibleNav.map(item => {
        const Icon = item.icon;
        const count = badgeFor(item.key);
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/dashboard"}
            className={({ isActive }) => `ht-nav-item ${isActive ? "active" : ""}`}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <Icon size={18} weight="regular" />
            <span className="flex-1">{item.label}</span>
            {count > 0 && (
              <span
                className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold flex items-center justify-center bg-red-500 text-white shadow-lg shadow-red-500/40"
                data-testid={`badge-${item.key}`}
              >
                {count > 99 ? "99+" : count}
              </span>
            )}
          </NavLink>
        );
      })}
    </nav>
  );
}

function UserTile({ user, initials, onLogout }) {
  // Iter209cx — `mt-auto` + `shrink-0` ancora il profilo in fondo alla
  // sidebar fissa a tutta altezza, l'area notifiche sopra fa lo scroll.
  return (
    <div className="mt-auto shrink-0 p-3 border-t border-white/10">
      <NavLink to="/settings" className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-white/5 transition-colors" data-testid="user-tile">
        <UserAvatar user={user} size={36} testId="user-tile-avatar" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-white truncate" data-testid="user-name">
            {user.title ? `${user.title} ` : ""}{user.first_name} {user.last_name}
          </div>
          <div className="text-xs" style={{ color: "#8b95a8" }}>
            {user.role}{user.is_supervisor && " · Supervisor"}
          </div>
        </div>
        {user.is_supervisor && <ShieldCheck size={16} color="#a78bfa" weight="fill" />}
      </NavLink>
      <button
        onClick={onLogout}
        data-testid="logout-btn"
        className="ht-nav-item w-full mt-1 hover:bg-red-500/20 hover:text-red-300"
      >
        <SignOut size={18} />
        <span>Esci</span>
      </button>
    </div>
  );
}

function Sidebar({ visibleNav, badgeFor, user, initials, onLogout, mobileOpen }) {
  // Iter209cx — Sidebar sempre `fixed`, altezza 100vh, scroll interno.
  // Desktop: visibile; Mobile: slide-in da sinistra via `translate-x`.
  // Il main content si offsetta con `md:ml-60` (width w-60 = 15rem).
  return (
    <aside
      className={`ht-sidebar w-60 flex flex-col fixed inset-y-0 left-0 z-50 h-screen overflow-y-auto overflow-x-hidden transition-transform duration-200 ${mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}
      data-testid="sidebar"
    >
      <SidebarBrand />
      <NavList visibleNav={visibleNav} badgeFor={badgeFor} />
      {(user.role === "professional" || ["tech_operator", "desk_operator", "accounting_operator"].includes(user.role)) && <SidebarCalendar />}
      <UserTile user={user} initials={initials} onLogout={onLogout} />
    </aside>
  );
}

function NotifBell() {
  const { summary, items, markRead } = useNotifications();
  const nav = useNavigate();
  const onNotifClick = useCallback(async (n) => {
    await markRead({ ids: [n._id] });
    if (n.target_url) nav(n.target_url);
  }, [markRead, nav]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="relative p-2 rounded-md hover:bg-[color:hsl(var(--surface-alt))] transition-colors" data-testid="notif-bell">
          <Bell size={20} weight="regular" />
          {summary.total > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center bg-red-500 text-white">
              {summary.total > 99 ? "99+" : summary.total}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notifiche</span>
          {summary.total > 0 && (
            <button
              onClick={() => markRead({ all: true })}
              className="text-xs font-normal text-muted-foreground hover:text-foreground"
              data-testid="mark-all-read"
            >
              Segna tutte come lette
            </button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.length === 0 && (
          <div className="p-6 text-center text-sm text-muted-foreground">Nessuna notifica</div>
        )}
        <div className="max-h-96 overflow-y-auto">
          {items.slice(0, 20).map(n => (
            <DropdownMenuItem
              key={n._id}
              onClick={() => onNotifClick(n)}
              className={`cursor-pointer p-3 border-b last:border-b-0 ${!n.is_read ? "bg-purple-50/50" : ""}`}
              data-testid={`notif-${n._id}`}
            >
              <div className="flex items-start gap-2 w-full">
                {!n.is_read && <span className="w-2 h-2 rounded-full bg-red-500 mt-1.5 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium leading-tight">{n.title}</div>
                  {n.body && <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</div>}
                  <div className="text-[10px] text-muted-foreground mt-1 uppercase tracking-wider">{relTime(n.created_at)}</div>
                </div>
              </div>
            </DropdownMenuItem>
          ))}
        </div>
        <DropdownMenuSeparator />
        <button
          onClick={() => nav("/notifications")}
          className="w-full text-center text-xs py-3 hover:bg-purple-50 text-purple-700 font-semibold transition-colors"
          data-testid="open-notifications-page"
        >
          Apri bacheca notifiche →
        </button>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function TopBar({ onOpenMobileMenu }) {
  return (
    <div className="sticky top-0 z-20 bg-background/80 backdrop-blur border-b flex items-center justify-between gap-2 px-4 md:px-8 py-3">
      <button
        onClick={onOpenMobileMenu}
        className="md:hidden p-2 -ml-2 rounded-md hover:bg-[color:hsl(var(--surface-alt))] transition-colors"
        data-testid="mobile-menu-btn"
        aria-label="Apri menu"
      >
        <List size={22} weight="bold" />
      </button>
      <div className="md:hidden font-display font-bold text-base tracking-tight">
        Housetag<span style={{ color: "#7c3aed" }}>.it</span>
      </div>
      <div className="flex-1 hidden md:block" />
      <NotifBell />
    </div>
  );
}

// ==================== Main Layout ====================

export default function Layout() {
  const { user, logout } = useAuth();
  const { summary, propertySummary, markRead } = useNotifications();
  const nav = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const chatbotPending = useChatbotPending(user);

  // Iter102: auto-mark all `target_key=properties` notifications when the
  // user enters the Immobili section. Ensures il badge sidebar della voce
  // "Immobili" e i pallini generali si azzerano immediatamente all'ingresso,
  // mentre i pallini sulle cartelle di servizio restano finché l'utente non
  // apre la specifica cartella (gestito da PropertyDetail / PropertyDocumentsSection).
  useEffect(() => {
    if (!user) return;
    if (!location.pathname.startsWith("/properties")) return;
    const propBadge = (summary?.per_page?.properties || 0) + (propertySummary?.total || 0);
    if (propBadge === 0) return;
    markRead({ target_key: "properties" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, user]);

  // Passkey enrollment modal: trigger onboarding se appena completato, altrimenti login
  const [passkeyTrigger, setPasskeyTrigger] = useState(null);
  useEffect(() => {
    if (!user) { setPasskeyTrigger(null); return; }
    const justOnboarded = sessionStorage.getItem("ht_just_onboarded") === "1";
    setPasskeyTrigger(justOnboarded ? "onboarding" : "login");
  }, [user?.id]);
  const handlePasskeyClose = useCallback(() => {
    sessionStorage.removeItem("ht_just_onboarded");
    setPasskeyTrigger(null);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Iter209dl — Web Push subscription al login (prompt UNA volta, non invasivo).
  // Al secondo accesso se già `granted` ri-sincronizza silenziosamente la
  // subscription col backend (user_id può essere cambiato dopo switch account).
  // Il flag su localStorage evita di chiedere il permesso ad ogni mount.
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { ensureWebPushRegistered, wireServiceWorkerMessages } = await import("@/lib/webPush");
        const askedKey = "ht.webPushAsked";
        const alreadyAsked = typeof window !== "undefined" && localStorage.getItem(askedKey) === "1";
        const permission = typeof Notification !== "undefined" ? Notification.permission : "default";
        if (permission === "granted") {
          await ensureWebPushRegistered({ promptIfDefault: false });
        } else if (permission === "default" && !alreadyAsked) {
          // Lieve delay così non appare simultaneo al toast di login.
          setTimeout(async () => {
            await ensureWebPushRegistered({ promptIfDefault: true });
            try { localStorage.setItem(askedKey, "1"); } catch (_) {}
          }, 1500);
        }
        // Listener per navigate da SW notificationclick quando client.navigate() non supportato
        wireServiceWorkerMessages((url) => {
          try { nav(url); } catch (_) {}
        });
      } catch (_) { /* no-op */ }
    })();
  }, [user?.id]);

  const initials = (user.first_name?.[0] || "") + (user.last_name?.[0] || "");

  // Iter208.18 — Fetch il `dashboard_type` della categoria professionale del PRO
  // (o del suo parent se sub-user) per abilitare voci NAV condizionali come
  // "My Domain" (solo agenzie immobiliari).
  const [dashboardType, setDashboardType] = useState("");
  useEffect(() => {
    if (!user) { setDashboardType(""); return; }
    if (user.role !== "professional") { setDashboardType(""); return; }
    if (!user.professional_category_id) { setDashboardType(""); return; }
    api.get("/professional-categories")
      .then((r) => {
        const cat = (r.data || []).find((c) => c._id === user.professional_category_id);
        setDashboardType(cat?.dashboard_type || "");
      })
      .catch(() => setDashboardType(""));
  }, [user?.id, user?.professional_category_id]);

  // Iter209da — Conteggio email non lette per il badge nella sidebar.
  // Polling leggero (/email/unread-count è un IMAP SEARCH UNSEEN) ogni
  // 60 s per operatori/admin con casella configurata. Il refresh viene
  // triggerato anche sui cambi route (es. dopo che Email.jsx ha letto
  // un messaggio) via listener `window.dispatchEvent('email:refresh')`.
  const [emailUnread, setEmailUnread] = useState(0);
  useEffect(() => {
    if (!user) { setEmailUnread(0); return; }
    const r = user.role || "";
    const canHaveMailbox = r === "admin" || r.endsWith("_operator");
    if (!canHaveMailbox) { setEmailUnread(0); return; }
    let alive = true;
    const fetchCount = () => {
      api.get("/email/unread-count")
        .then((res) => { if (alive) setEmailUnread(Number(res.data?.count || 0)); })
        .catch(() => {});
    };
    fetchCount();
    const interval = setInterval(fetchCount, 60_000);
    const onRefresh = () => fetchCount();
    const onCount = (e) => {
      const n = Number(e?.detail?.count);
      if (Number.isFinite(n)) setEmailUnread(n);
    };
    window.addEventListener("email:refresh", onRefresh);
    window.addEventListener("email:count", onCount);
    return () => {
      alive = false;
      clearInterval(interval);
      window.removeEventListener("email:refresh", onRefresh);
      window.removeEventListener("email:count", onCount);
    };
  }, [user?.id, user?.role]);

  // Iter209do — Conteggio feedback beta non letti (solo admin) per badge sidebar.
  // Polling leggero ogni 60 s via `/admin/feedback/unread-count` (count-only).
  // Refresh immediato triggerato da `window.dispatchEvent('feedback:refresh')`
  // in AdminFeedback.jsx dopo cambio stato (new → in_progress/resolved).
  const [feedbackUnread, setFeedbackUnread] = useState(0);
  useEffect(() => {
    if (!user || user.role !== "admin") { setFeedbackUnread(0); return; }
    let alive = true;
    const fetchCount = () => {
      api.get("/admin/feedback/unread-count")
        .then((res) => { if (alive) setFeedbackUnread(Number(res.data?.count || 0)); })
        .catch(() => {});
    };
    fetchCount();
    const interval = setInterval(fetchCount, 60_000);
    const onRefresh = () => fetchCount();
    window.addEventListener("feedback:refresh", onRefresh);
    return () => {
      alive = false;
      clearInterval(interval);
      window.removeEventListener("feedback:refresh", onRefresh);
    };
  }, [user?.id, user?.role]);

  const visibleNav = useMemo(
    () => NAV.filter(n => {
      const roleOk = canSee(n.roles, user, { denySupervisor: n.denySupervisor }) || (n.allowSupervisor && user.is_supervisor && !n.denySupervisor);
      if (!roleOk) return false;
      // Iter208.18 — filtro dashboard_type opzionale
      if (n.requiresDashboardType && dashboardType !== n.requiresDashboardType) return false;
      // Iter209al / Iter209ao — La voce "Email" ha senso solo per
      // admin HouseTag e operatori (che possono avere una casella
      // @housetag.it associata). I `professional` (pro master) NON
      // hanno caselle nell'org Zoho — l'email è gestita dal loro
      // MyDomain. Escludiamo quindi pro + tutti gli altri ruoli
      // senza mailbox potenziale.
      if (n.key === "email") {
        const r = user.role || "";
        const canHaveMailbox = r === "admin" || r.endsWith("_operator");
        if (!canHaveMailbox) return false;
      }
      return true;
    }),
    [user, dashboardType],
  );

  const badgeFor = useCallback(
    (key) => {
      // Iter95: nascondi il pallino rosso sulla voce "I miei compensi"
      if (key === "my-compensations") return 0;
      if (key === "admin-chatbot") return chatbotPending;
      // Iter209da — Voce Email: conteggio IMAP UNSEEN (polling).
      if (key === "email") return emailUnread;
      // Iter209do — Voce Feedback Beta: feedback con status='new' (admin only).
      if (key === "admin-feedback") return feedbackUnread;
      // Iter93: la voce "Immobili" mostra il totale delle notifiche non
      // lette collegate a property_id (consegne documento, ecc.). Somma
      // eventuali altre notifiche con target_key=properties per non
      // perdere conteggi legacy.
      if (key === "properties") {
        const perPage = summary?.per_page?.properties || 0;
        const perProperty = propertySummary?.total || 0;
        return Math.max(perPage, perProperty);
      }
      return summary?.per_page?.[key] || 0;
    },
    [chatbotPending, summary, propertySummary, emailUnread, feedbackUnread],
  );

  const handleLogout = useCallback(() => {
    logout();
    nav("/");
    // Iter207.23 — Dopo il redirect alla homepage, forza lo scroll in cima:
    // altrimenti la landing eredita la posizione di scroll della pagina
    // precedente (es. bottom-of-page se l'utente era in fondo al kanban).
    // `setTimeout` di 1 tick lascia a React il tempo di renderizzare la nuova route.
    setTimeout(() => {
      try { window.scrollTo({ top: 0, left: 0, behavior: "instant" }); }
      catch { window.scrollTo(0, 0); }
    }, 0);
  }, [logout, nav]);

  return (
    <div className="flex min-h-screen">
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/60 z-40"
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-nav-backdrop"
        />
      )}
      <Sidebar
        visibleNav={visibleNav}
        badgeFor={badgeFor}
        user={user}
        initials={initials}
        onLogout={handleLogout}
        mobileOpen={mobileOpen}
      />
      <main className="flex-1 overflow-x-hidden md:ml-60" data-testid="app-main">
        <TopBar onOpenMobileMenu={() => setMobileOpen(true)} />
        <div className="max-w-[1600px] mx-auto p-6 md:p-8">
          <Outlet />
        </div>
      </main>

      {passkeyTrigger && (
        <PasskeyEnrollModal user={user} trigger={passkeyTrigger} onClose={handlePasskeyClose} />
      )}

      {/* Iter208.5 — Feedback Beta floating button (solo utenti autenticati) */}
      <FeedbackButton />

      {/* Iter209aq — Onboarding tutorial "prime 3 aree" pro master. */}
      <ProOnboardingModal />

    </div>
  );
}
